import { useRef, useEffect, useState } from 'react'
import { StickyContainer, StickyWrapper } from '@/common/sticky'
import styles from './index.module.less'
import classnames from 'classnames'
import { Tabs } from 'antd-mobile'
import Context, { langMap } from './context'
import useLangConfig from '@/hooks/business/useLangConfig'
import List from './list'
import { createTrackConfig } from './trackConfig'
import { useWithReactFunctionalTrack } from '@du/analytics-sdk'
import { IUserType } from '../../typing'
import { isInApp } from '@/utils'
let $track: any
export enum SpuType {
  商品货号 = '商品货号',
  SPUID = 'SPUID',
}

interface IProps {
  tabList: {
    name: string
    spuIdType: SpuType
    spuIds: string
  }[]
  editConfig: any
  expired?: boolean
  tabTextColorActive?: string
  tabTextColor?: string
  background?: any
  tabItemTitleBg?: string
  tabBgColor?: string
  tabCount?: number
  renderUserType?: IUserType
}

function getTabs(props: any) {
  const { tabList } = props
  const tabs =
    tabList.map(({ name }, index) => ({
      title: name,
      index,
    })) || []
  return tabs
}

export default function ProductFlow(props: IProps) {
  // 埋点绑定
  const functionalProps = { ...props }
  // 使用hook useEffect api ，当ref有值则组件渲染结束，props可挂载并执行$track函数
  const functionalRef = useRef(null)
  useWithReactFunctionalTrack({
    functionalRef,
    functionalProps,
    useEffect,
    createTrackConfig,
  })

  const {
    tabList,
    tabTextColorActive,
    tabTextColor,
    tabItemTitleBg,
    editConfig,
    background,
    expired,
    tabBgColor,
    tabCount,
    renderUserType,
  } = props

  const { lang, currencyCode, langConfig } = useLangConfig({
    editConfig,
    langConfigMap: langMap,
  })

  const [page, setPage] = useState(0)
  function handleTabChange(_tab: any, index: number) {
    setPage(index)
  }

  // 点击tab 定位商品流
  function handleTabClick(tab: any, index: number) {
    const docScrollTop = document.documentElement.scrollTop
    const eleTop = document.querySelector(`.product-view-box-list-${index}`).getBoundingClientRect()
      .top
    const top = expired ? docScrollTop + eleTop - 40 : docScrollTop + eleTop - 90
    document.documentElement.scrollTo({
      // tab距视口顶部距离和tab本身高度
      top,
      behavior: 'smooth',
    })

    // fix: 多次组件渲染，$track 方法丢失
    if ((functionalProps as any).$track) {
      $track = (functionalProps as any).$track
    }
    try {
      $track('poizon_trade_rank_list_tab_click', { index, title: tab.title })
    } catch {
      /** noop */
    }
  }

  // 商品流滚动距离视口底部的距离，之内（小于40大于0）确认滚动tab
  const scrollTabActivePosition = 40

  useEffect(() => {
    const options = {
      rootMargin: `-${scrollTabActivePosition}px 0px 0px 0px`,
      threshold: [
        0,
        0.1,
        0.11,
        0.12,
        0.13,
        0.14,
        0.15,
        0.16,
        0.17,
        0.18,
        0.19,
        0.2,
        0.21,
        0.22,
        0.23,
        0.24,
        0.25,
        0.26,
        0.27,
        0.28,
        0.29,
        0.3,
        0.31,
        0.32,
        0.33,
        0.34,
        0.35,
        0.36,
        0.37,
        0.38,
        0.39,
        0.4,
        0.41,
        0.42,
        0.43,
        0.44,
        0.45,
        0.46,
        0.47,
        0.48,
        0.49,
        0.5,
        0.51,
        0.52,
        0.53,
        0.54,
        0.55,
        0.56,
        0.57,
        0.58,
        0.59,
        0.6,
        0.61,
        0.62,
        0.63,
        0.64,
        0.65,
        0.66,
        0.67,
        0.68,
        0.69,
        0.7,
        0.71,
        0.72,
        0.73,
        0.74,
        0.75,
        0.76,
        0.77,
        0.78,
        0.79,
        0.8,
        0.81,
        0.82,
        0.83,
        0.84,
        0.85,
        0.86,
        0.87,
        0.88,
        0.89,
        0.9,
        0.91,
        0.92,
        0.93,
        0.94,
        0.95,
        0.96,
        0.97,
        0.98,
        0.99,
        1,
      ],
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entrie) => {
        // 当目标元素高度没有视口高度大时
        if (entrie.boundingClientRect.height < entrie.rootBounds.height) {
          const flag =
            entrie.boundingClientRect.top < scrollTabActivePosition + 100 &&
            entrie.boundingClientRect.top > 0
          if (!flag) return
          setPage(+(entrie.target as any).dataset.index)
          return
        }
        // 当目标元素高度比视口高度大时
        const ratio = entrie.rootBounds.height / entrie.boundingClientRect.height
        if (entrie.intersectionRatio < ratio * 0.7) return
        // 目标元素占满视口区域70%即认为激活当前tab
        setPage(+(entrie.target as any).dataset.index)
      })
    }, options)

    ;[...(document.querySelectorAll(`.product-view-box`) as any)].forEach((ele) =>
      observer.observe(ele)
    )
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={classnames({
        [styles['product-flow']]: true,
        [styles['is-seller']]: renderUserType === IUserType.seller,
        'product-flow': true,
      })}
      ref={functionalRef}
    >
      <Context.Provider
        value={{
          lang,
          currencyCode,
          langConfig,
        }}
      >
        <StickyContainer>
          <Tabs
            page={page}
            tabs={getTabs({ tabList })}
            animated={false}
            onChange={handleTabChange}
            onTabClick={handleTabClick}
            tabBarActiveTextColor={tabTextColorActive}
            tabBarInactiveTextColor={tabTextColor}
            tabBarUnderlineStyle={{ opacity: 0, display: 'none' }}
            renderTabBar={(props) => (
              <StickyWrapper>
                {({ style, isSticky }) => {
                  return (
                    <div
                      className={classnames({
                        [styles['module-venue-tab-default-bar']]: true,
                        [styles['is-sticky']]: isSticky || renderUserType === IUserType.seller,
                        [styles['is-expired']]: expired,
                        [styles['out-app']]: !isInApp,
                        'module-venue-tab-default-bar': true,
                      })}
                      style={{
                        ...style,
                        zIndex: 1,
                        backgroundColor: isSticky
                          ? tabBgColor
                          : // 如果是卖家 tab 背景色和 商品流渐变色保持一致
                          renderUserType === IUserType.seller
                          ? background?.feedBackColor[0] || '#138C90'
                          : '',
                      }}
                    >
                      {/* 超出3个滚动 */}
                      <Tabs.DefaultTabBar {...props} page={tabCount} />
                    </div>
                  )
                }}
              </StickyWrapper>
            )}
          ></Tabs>
          <style>
            {/* Tab 组件样式调整 */}
            {`
            .am-tabs-default-bar-prevpage,.am-tabs-default-bar-nextpage {
              display: none !important;
            }
            .am-tabs-default-bar {
              background-color: transparent !important;
            }
            .am-tabs-default-bar-tab {
              font-family: PingFang HK;
              font-style: normal;
              font-weight: 300;
              font-size: 14px;
              line-height: 20px;
              text-align: center;
              color: #004B4E;
            }
            .am-tabs-default-bar-tab-active {
              font-family: PingFang HK;
              font-style: normal;
              font-weight: 500;
              font-size: 16px;
              line-height: 22px;
              color: #FFFFFF;
            }
            .product-flow .module-venue-tab-default-bar .am-tabs-default-bar-tab::after {
              background-color: transparent !important;
            }
          `}
          </style>
          <div
            className={styles['product-view-box-wrapper']}
            style={{
              background: `linear-gradient(180deg, ${
                background?.feedBackColor[0] || '#138C90'
              } 0%,  ${background?.feedBackColor[0] || '#138C90'} 67.05%)`,
            }}
          >
            {tabList.map((v, index) => {
              return (
                <div
                  key={index + 'type' + renderUserType}
                  className={`product-view-box`}
                  data-index={index}
                >
                  <div
                    className={classnames({
                      [styles['title']]: true,
                    })}
                  >
                    {v.name}
                    <img
                      className={styles['title-bg']}
                      src={
                        tabItemTitleBg ||
                        'https://cdn.poizon.com/node-common/5481d660dce2398289911bcf16f2ab24.png'
                      }
                      alt=""
                    />
                  </div>
                  <List
                    index={index}
                    editConfig={props.editConfig}
                    renderUserType={renderUserType}
                    type={v.spuIdType}
                    list={v.spuIds.split(',')}
                  />
                </div>
              )
            })}
          </div>
        </StickyContainer>
      </Context.Provider>
    </div>
  )
}
